import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useReactToPrint } from 'react-to-print';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Button, Chip, CircularProgress,
  Alert, Grid, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Tab, Tabs, Autocomplete, IconButton, Tooltip, ButtonGroup, Checkbox
} from '@mui/material';
import { LocalHospital, PlayArrow, CheckCircle, Assignment, People, NavigateBefore, NavigateNext, Today, MedicalServices, Dashboard, ListAlt, Print } from '@mui/icons-material';
import { treatmentCoordinatorAPI, therapistAPI, treatmentAPI } from '../services/api';
import TherapistManagement from './TherapistManagement';
import OPDTreatmentCoordinator from './OPDTreatmentCoordinator';

function TreatmentCoordinatorDashboard() {
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState(0); // 0 = Individual Treatments, 1 = Consolidated Dashboard
  const [treatments, setTreatments] = useState([]);
  const [stats, setStats] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [therapists, setTherapists] = useState([]);
  const [availableTherapists, setAvailableTherapists] = useState([]);
  const [treatmentTypes, setTreatmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterTimeSlot, setFilterTimeSlot] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const [assignDialog, setAssignDialog] = useState({
    open: false,
    itemId: null,
    treatmentId: null,
    treatmentName: '',
    assignedTo: '',
    therapistId: '',
    scheduledDate: new Date().toISOString().split('T')[0],
    scheduledStartTime: '09:00',
    durationMinutes: 30
  });

  const [nextAvailableSlots, setNextAvailableSlots] = useState([]);
  const [showSlotFull, setShowSlotFull] = useState(false);
  const [completeDialog, setCompleteDialog] = useState({ open: false, executionId: null, isBulk: false, ids: [], notes: '' });
  const [directCompleteDialog, setDirectCompleteDialog] = useState({ open: false, treatmentPlanItemId: null, isBulk: false, ids: [], notes: '' });
  const [selectedTreatments, setSelectedTreatments] = useState([]);

  useEffect(() => {
    setSelectedTreatments([]);
  }, [selectedDate, filterTimeSlot, viewMode, activeTab]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedTreatments(selectableTreatments);
    } else {
      setSelectedTreatments([]);
    }
  };

  const handleRowSelect = (treatment) => {
    setSelectedTreatments(prev => {
      const isSelected = prev.some(t => t.treatment_plan_item_id === treatment.treatment_plan_item_id);
      if (isSelected) {
        return prev.filter(t => t.treatment_plan_item_id !== treatment.treatment_plan_item_id);
      } else {
        return [...prev, treatment];
      }
    });
  };

  const handleBulkAssignOpen = () => {
    if (selectedTreatments.length === 0) return;
    const firstTreatment = selectedTreatments[0];
    const treatmentInfo = treatmentTypes.find(t => t.id === firstTreatment.treatment_id);
    const duration = treatmentInfo?.duration_minutes || 30;

    setAssignDialog({
      open: true,
      isBulk: true,
      itemIds: selectedTreatments.map(t => t.treatment_plan_item_id),
      treatmentId: null,
      treatmentName: `${selectedTreatments.length} Selected Treatments`,
      assignedTo: firstTreatment.assigned_to || '',
      therapistId: '',
      scheduledDate: new Date().toISOString().split('T')[0],
      scheduledStartTime: '09:00',
      durationMinutes: duration
    });

    loadAvailableTherapists(new Date().toISOString().split('T')[0], '09:00', duration);
  };

  const handleBulkInitiate = async () => {
    const executionIds = selectedTreatments
      .filter(t => t.execution_id && t.status === 'pending')
      .map(t => t.execution_id);

    if (executionIds.length === 0) {
      setError('No pending assigned treatments selected');
      return;
    }

    try {
      await treatmentCoordinatorAPI.bulkUpdateStatus({
        ids: executionIds,
        action: 'initiate'
      });
      setSuccess('Selected treatments initiated successfully');
      setSelectedTreatments([]);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to initiate selected treatments');
    }
  };

  const handleBulkCompleteOpen = () => {
    const ids = selectedTreatments
      .filter(t => t.execution_id && (t.status === 'initiated' || t.status === 'in_progress'))
      .map(t => t.execution_id);

    if (ids.length === 0) {
      setError('No initiated or in-progress treatments selected');
      return;
    }

    setCompleteDialog({
      open: true,
      executionId: null,
      isBulk: true,
      ids,
      notes: ''
    });
  };

  const handleBulkDirectCompleteOpen = () => {
    const ids = selectedTreatments
      .filter(t => !t.execution_id)
      .map(t => t.treatment_plan_item_id);

    if (ids.length === 0) {
      setError('No unassigned treatments selected');
      return;
    }

    setDirectCompleteDialog({
      open: true,
      treatmentPlanItemId: null,
      isBulk: true,
      ids,
      notes: ''
    });
  };

  const treatmentDayPrintRef = useRef();
  const handlePrintTreatmentDay = useReactToPrint({
    contentRef: treatmentDayPrintRef,
    documentTitle: `Treatment Plan - ${selectedDate}`,
    pageStyle: '@page { size: A4 landscape; margin: 10mm; }'
  });

  useEffect(() => {
    loadData();
  }, [selectedDate, viewMode, activeTab]);

  const loadData = async () => {
    if (activeTab !== 0) return; // Only load for IPD treatments tab

    setLoading(true);
    try {
      if (viewMode === 0) {
        const [treatmentsRes, statsRes, therapistsRes, treatmentTypesRes] = await Promise.all([
          treatmentCoordinatorAPI.getTodaysTreatments(selectedDate),
          treatmentCoordinatorAPI.getTodaysStats(selectedDate),
          therapistAPI.getAll({ active: 'true' }),
          treatmentAPI.getAll()
        ]);
        setTreatments(treatmentsRes.data.data || []);
        setStats(statsRes.data.data || []);
        setTherapists(therapistsRes.data.data || []);
        setTreatmentTypes(treatmentTypesRes.data.data || []);
      } else {
        const [dashboardRes, therapistsRes] = await Promise.all([
          treatmentCoordinatorAPI.getDashboard(selectedDate),
          therapistAPI.getAll({ active: 'true' })
        ]);
        setDashboardData(dashboardRes.data.data || dashboardRes.data);
        setTherapists(therapistsRes.data.data || []);
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignOpen = async (treatment) => {
    // Get treatment duration
    const treatmentInfo = treatmentTypes.find(t => t.id === treatment.treatment_id);
    const duration = treatmentInfo?.duration_minutes || 30;

    setAssignDialog({
      open: true,
      itemId: treatment.treatment_plan_item_id,
      treatmentId: treatment.treatment_id,
      treatmentName: treatment.treatment_name,
      assignedTo: treatment.assigned_to || '',
      therapistId: '',
      scheduledDate: new Date().toISOString().split('T')[0],
      scheduledStartTime: '09:00',
      durationMinutes: duration
    });

    // Load available therapists
    loadAvailableTherapists(new Date().toISOString().split('T')[0], '09:00', duration);
  };

  const loadAvailableTherapists = async (date, time, duration) => {
    try {
      const response = await therapistAPI.getAvailable({
        date,
        startTime: time,
        durationMinutes: duration
      });
      setAvailableTherapists(response.data.data || []);
    } catch (err) {
      console.error('Failed to load available therapists:', err);
      setAvailableTherapists([]);
    }
  };

  const handleDateTimeChange = (field, value) => {
    const updatedDialog = { ...assignDialog, [field]: value };
    setAssignDialog(updatedDialog);

    // Reload available therapists when date or time changes
    if (field === 'scheduledDate' || field === 'scheduledStartTime') {
      loadAvailableTherapists(
        updatedDialog.scheduledDate,
        updatedDialog.scheduledStartTime,
        updatedDialog.durationMinutes
      );
    }
  };

  const handleAssignSubmit = async () => {
    try {
      setShowSlotFull(false);
      setNextAvailableSlots([]);

      if (assignDialog.isBulk) {
        await treatmentCoordinatorAPI.bulkAssign({
          treatmentPlanItemIds: assignDialog.itemIds,
          assignedTo: assignDialog.assignedTo,
          therapistId: assignDialog.therapistId,
          scheduledDate: assignDialog.scheduledDate,
          scheduledStartTime: assignDialog.scheduledStartTime + ':00'
        });
        setSuccess('Selected treatments assigned and calendar booked successfully');
        setSelectedTreatments([]);
      } else {
        await treatmentCoordinatorAPI.assignTreatment(assignDialog.itemId, {
          assignedTo: assignDialog.assignedTo,
          therapistId: assignDialog.therapistId,
          scheduledDate: assignDialog.scheduledDate,
          scheduledStartTime: assignDialog.scheduledStartTime + ':00',
          treatmentId: assignDialog.treatmentId
        });
        setSuccess('Treatment assigned and calendar booked successfully');
      }
      setAssignDialog({
        open: false,
        itemId: null,
        isBulk: false,
        itemIds: [],
        treatmentId: null,
        treatmentName: '',
        assignedTo: '',
        therapistId: '',
        scheduledDate: new Date().toISOString().split('T')[0],
        scheduledStartTime: '09:00',
        durationMinutes: 30
      });
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorData = err.response?.data?.errors;

      // Check if it's a slot full error with next available slots
      if (errorData?.slotFull && errorData?.nextAvailableSlots) {
        setShowSlotFull(true);
        setNextAvailableSlots(errorData.nextAvailableSlots);
        setError(`${err.response?.data?.message || 'Slot is full'}. Please select from the available slots below.`);
      } else {
        setError(err.response?.data?.message || 'Failed to assign treatment');
      }
    }
  };

  const handleSelectNextSlot = (slot) => {
    setAssignDialog(prev => ({
      ...prev,
      scheduledDate: slot.date,
      scheduledStartTime: slot.startTime.substring(0, 5) // Remove seconds
    }));
    setShowSlotFull(false);
    setNextAvailableSlots([]);
    setError('');
  };

  const handleInitiate = async (executionId) => {
    try {
      await treatmentCoordinatorAPI.initiateTreatment(executionId);
      setSuccess('Treatment initiated successfully');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to initiate treatment');
    }
  };

  const handleCompleteOpen = (executionId) => {
    setCompleteDialog({ open: true, executionId, isBulk: false, ids: [], notes: '' });
  };

  const handleCompleteSubmit = async () => {
    try {
      if (completeDialog.isBulk) {
        await treatmentCoordinatorAPI.bulkUpdateStatus({
          ids: completeDialog.ids,
          action: 'complete',
          notes: completeDialog.notes
        });
        setSuccess('Selected treatments completed successfully');
        setSelectedTreatments([]);
      } else {
        await treatmentCoordinatorAPI.completeTreatment(completeDialog.executionId, { notes: completeDialog.notes });
        setSuccess('Treatment completed successfully');
      }
      setCompleteDialog({ open: false, executionId: null, isBulk: false, ids: [], notes: '' });
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to complete treatment');
    }
  };

  const handleDirectCompleteOpen = (treatmentPlanItemId) => {
    setDirectCompleteDialog({ open: true, treatmentPlanItemId, isBulk: false, ids: [], notes: '' });
  };

  const handleDirectCompleteSubmit = async () => {
    try {
      if (directCompleteDialog.isBulk) {
        await treatmentCoordinatorAPI.bulkUpdateStatus({
          ids: directCompleteDialog.ids,
          action: 'complete-direct',
          notes: directCompleteDialog.notes
        });
        setSuccess('Selected treatments completed successfully');
        setSelectedTreatments([]);
      } else {
        await treatmentCoordinatorAPI.directCompleteTreatment(directCompleteDialog.treatmentPlanItemId, { notes: directCompleteDialog.notes });
        setSuccess('Treatment marked as completed');
      }
      setDirectCompleteDialog({ open: false, treatmentPlanItemId: null, isBulk: false, ids: [], notes: '' });
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to complete treatment');
    }
  };

  const filteredTreatments = filterTimeSlot === 'all'
    ? treatments
    : treatments.filter(t => t.time_slot === filterTimeSlot);

  const getStatusColor = (status) => {
    if (!status || status === 'pending') return 'warning';
    if (status === 'initiated' || status === 'in_progress') return 'info';
    if (status === 'completed') return 'success';
    return 'default';
  };

  const getTimeSlotLabel = (slot) => {
    const labels = { Satkriya: 'Satkriya', Morning: 'Morning', Evening: 'Evening', Night: 'Night' };
    return labels[slot] || slot;
  };

  const getDateLabel = () => {
    const today = new Date().toISOString().split('T')[0];
    const selected = new Date(selectedDate);
    const todayDate = new Date(today);

    if (selectedDate === today) return 'Today';
    if (selected < todayDate) return 'Past Date';
    return 'Future Date';
  };

  const isFutureDate = () => {
    const today = new Date().toISOString().split('T')[0];
    return selectedDate > today;
  };

  const selectableTreatments = filteredTreatments.filter(t => !isFutureDate() || selectedDate === new Date().toISOString().split('T')[0]);

  const handlePreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  if (loading && activeTab === 0) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ mb: 3 }}>
        <Tab label="IPD / Day Care Treatments" icon={<LocalHospital />} iconPosition="start" />
        <Tab label="OPD Treatments" icon={<MedicalServices />} iconPosition="start" />
        <Tab label="Therapist Management" icon={<People />} iconPosition="start" />
      </Tabs>

      {activeTab === 0 && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LocalHospital sx={{ fontSize: 40, color: 'secondary.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">Treatment Coordinator Dashboard</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {getDateLabel()} - {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ButtonGroup variant="outlined" size="small">
                  <Tooltip title="Previous Day">
                    <IconButton onClick={handlePreviousDay}>
                      <NavigateBefore />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Today">
                    <IconButton onClick={handleToday}>
                      <Today />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Next Day">
                    <IconButton onClick={handleNextDay}>
                      <NavigateNext />
                    </IconButton>
                  </Tooltip>
                </ButtonGroup>
                <TextField
                  type="date"
                  label="Select Date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 180 }}
                  size="small"
                />
                <Tooltip title="Print Day Treatment Plan">
                  <Button variant="outlined" size="small" startIcon={<Print />}
                    onClick={handlePrintTreatmentDay} disabled={treatments.length === 0}>
                    Print
                  </Button>
                </Tooltip>
              </Box>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={viewMode} onChange={(e, newValue) => setViewMode(newValue)}>
                <Tab icon={<ListAlt />} iconPosition="start" label="Individual Treatments" />
                <Tab icon={<Dashboard />} iconPosition="start" label="Consolidated Dashboard" />
              </Tabs>
            </Box>

            {viewMode === 0 ? (
              <>
                {(selectedDate !== new Date().toISOString().split('T')[0]) && (
                  <Alert severity={isFutureDate() ? 'info' : 'warning'} sx={{ mb: 2 }}>
                    {isFutureDate()
                      ? 'Viewing scheduled treatments for a future date. Initiate and complete actions may be limited.'
                      : 'Viewing historical treatment data. All shown statuses are final.'}
                  </Alert>
                )}

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {stats.map((stat) => (
                    <Grid item xs={12} sm={4} key={stat.time_slot}>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2" fontWeight="bold">{getTimeSlotLabel(stat.time_slot)}</Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                          <Box>
                            <Typography variant="h5" color="success.main">{stat.completed}</Typography>
                            <Typography variant="caption">Completed</Typography>
                          </Box>
                          <Box>
                            <Typography variant="h5" color="info.main">{stat.initiated}</Typography>
                            <Typography variant="caption">Initiated</Typography>
                          </Box>
                          <Box>
                            <Typography variant="h5" color="warning.main">{stat.pending}</Typography>
                            <Typography variant="caption">Pending</Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                <Tabs value={filterTimeSlot} onChange={(e, val) => setFilterTimeSlot(val)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tab label="All" value="all" />
                  <Tab label="Satkriya" value="Satkriya" />
                  <Tab label="Morning" value="Morning" />
                  <Tab label="Evening" value="Evening" />
                  <Tab label="Night" value="Night" />
                </Tabs>

                {selectedTreatments.length > 0 && (
                  <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50', border: 1, borderColor: 'primary.light', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {selectedTreatments.length} treatment(s) selected
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<Assignment />}
                        onClick={handleBulkAssignOpen}
                      >
                        Assign Selected
                      </Button>
                      <Button
                        variant="contained"
                        color="info"
                        size="small"
                        startIcon={<PlayArrow />}
                        onClick={handleBulkInitiate}
                      >
                        Initiate Selected
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckCircle />}
                        onClick={handleBulkCompleteOpen}
                      >
                        Complete Selected
                      </Button>
                      <Button
                        variant="outlined"
                        color="success"
                        size="small"
                        startIcon={<CheckCircle />}
                        onClick={handleBulkDirectCompleteOpen}
                      >
                        Direct Complete Selected
                      </Button>
                    </Box>
                  </Paper>
                )}

                {filteredTreatments.length === 0 ? (
                  <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography color="text.secondary">No treatments scheduled for this time slot</Typography>
                  </Paper>
                ) : (
                  <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell style={{ width: '50px' }}>
                            <Checkbox
                              indeterminate={selectedTreatments.length > 0 && selectedTreatments.length < selectableTreatments.length}
                              checked={selectableTreatments.length > 0 && selectedTreatments.length === selectableTreatments.length}
                              onChange={handleSelectAllClick}
                            />
                          </TableCell>
                          <TableCell><strong>Time Slot</strong></TableCell>
                          <TableCell><strong>Patient</strong></TableCell>
                          <TableCell><strong>Room</strong></TableCell>
                          <TableCell><strong>Treatment</strong></TableCell>
                          <TableCell><strong>Assigned To</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredTreatments.map((treatment) => {
                          const isSelected = selectedTreatments.some(t => t.treatment_plan_item_id === treatment.treatment_plan_item_id);
                          return (
                            <TableRow key={`${treatment.treatment_plan_item_id}-${treatment.execution_id || 'new'}`} hover selected={isSelected}>
                              <TableCell>
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => handleRowSelect(treatment)}
                                />
                              </TableCell>
                              <TableCell><Chip label={getTimeSlotLabel(treatment.time_slot)} size="small" color="secondary" /></TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">{treatment.first_name} {treatment.last_name}</Typography>
                              <Typography variant="caption" color="text.secondary">{treatment.patient_code}</Typography>
                            </TableCell>
                            <TableCell>{treatment.room_number}</TableCell>
                            <TableCell>{treatment.treatment_name}</TableCell>
                            <TableCell>
                              {treatment.assigned_to_name || <Typography variant="caption" color="text.secondary">Not assigned</Typography>}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={treatment.status || 'Pending'}
                                color={getStatusColor(treatment.status)}
                                size="small"
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </TableCell>
                            <TableCell>
                              {isFutureDate() || selectedDate === new Date().toISOString().split('T')[0] ? (
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Assignment />}
                                    onClick={() => handleAssignOpen(treatment)}
                                  >
                                    {treatment.assigned_to ? 'Reassign' : 'Assign'}
                                  </Button>
                                  {treatment.execution_id && treatment.status === 'pending' && (
                                    <Button
                                      variant="contained"
                                      color="info"
                                      size="small"
                                      startIcon={<PlayArrow />}
                                      onClick={() => handleInitiate(treatment.execution_id)}
                                    >
                                      Initiate
                                    </Button>
                                  )}
                                  {treatment.execution_id && (treatment.status === 'initiated' || treatment.status === 'in_progress') && (
                                    <Button
                                      variant="contained"
                                      color="success"
                                      size="small"
                                      startIcon={<CheckCircle />}
                                      onClick={() => handleCompleteOpen(treatment.execution_id)}
                                    >
                                      Complete
                                    </Button>
                                  )}
                                  {!treatment.execution_id && treatment.patient_type === 'ipd' && (
                                    <Button
                                      variant="outlined"
                                      color="success"
                                      size="small"
                                      startIcon={<CheckCircle />}
                                      onClick={() => handleDirectCompleteOpen(treatment.treatment_plan_item_id)}
                                    >
                                      Mark Done
                                    </Button>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Historical record
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        )})}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            ) : (
              <>
                {/* Consolidated Dashboard View */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                      <Typography variant="subtitle2">Total Patients</Typography>
                      <Typography variant="h3" fontWeight="bold">{dashboardData?.summary?.total_patients || 0}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, bgcolor: 'info.main', color: 'white' }}>
                      <Typography variant="subtitle2">Total Treatments</Typography>
                      <Typography variant="h3" fontWeight="bold">{dashboardData?.summary?.total_treatments || 0}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, bgcolor: 'success.main', color: 'white' }}>
                      <Typography variant="subtitle2">Completed</Typography>
                      <Typography variant="h3" fontWeight="bold">{dashboardData?.summary?.completed_treatments || 0}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, bgcolor: 'secondary.main', color: 'white' }}>
                      <Typography variant="subtitle2">Initiated</Typography>
                      <Typography variant="h3" fontWeight="bold">{dashboardData?.summary?.initiated_treatments || 0}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, bgcolor: 'warning.main', color: 'white' }}>
                      <Typography variant="subtitle2">Pending</Typography>
                      <Typography variant="h3" fontWeight="bold">{dashboardData?.summary?.pending_treatments || 0}</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Time Slot Summary */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Time Slot Summary</Typography>
                    {dashboardData?.timeSlotSummary?.length === 0 ? (
                      <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                        <Typography color="text.secondary">No time slot data available</Typography>
                      </Paper>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell><strong>Time Slot</strong></TableCell>
                              <TableCell align="right"><strong>Patients</strong></TableCell>
                              <TableCell align="right"><strong>Total Treatments</strong></TableCell>
                              <TableCell align="right"><strong>Completed</strong></TableCell>
                              <TableCell align="right"><strong>Initiated</strong></TableCell>
                              <TableCell align="right"><strong>Pending</strong></TableCell>
                              <TableCell align="right"><strong>Assigned</strong></TableCell>
                              <TableCell align="right"><strong>Progress</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dashboardData?.timeSlotSummary?.map((row) => (
                              <TableRow key={row.time_slot} hover>
                                <TableCell>
                                  <Chip label={getTimeSlotLabel(row.time_slot)} color="secondary" size="small" />
                                </TableCell>
                                <TableCell align="right">{row.patient_count}</TableCell>
                                <TableCell align="right">{row.treatment_count}</TableCell>
                                <TableCell align="right">
                                  <Chip label={row.completed} color="success" size="small" />
                                </TableCell>
                                <TableCell align="right">
                                  <Chip label={row.initiated} color="info" size="small" />
                                </TableCell>
                                <TableCell align="right">
                                  <Chip label={row.pending} color="warning" size="small" />
                                </TableCell>
                                <TableCell align="right">{row.assigned}</TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                                    {row.treatment_count > 0 ? Math.round((row.completed / row.treatment_count) * 100) : 0}%
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>

                {/* Consolidated Items */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Consolidated Treatment Items (Day-wise Total)</Typography>
                    {dashboardData?.consolidatedItems?.length === 0 ? (
                      <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                        <Typography color="text.secondary">No consolidated data available</Typography>
                      </Paper>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell><strong>Treatment</strong></TableCell>
                              <TableCell align="right"><strong>Patients</strong></TableCell>
                              <TableCell align="right"><strong>Total Count</strong></TableCell>
                              <TableCell align="right"><strong>Completed</strong></TableCell>
                              <TableCell align="right"><strong>Initiated</strong></TableCell>
                              <TableCell align="right"><strong>Pending</strong></TableCell>
                              <TableCell align="right"><strong>Assigned</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dashboardData?.consolidatedItems?.map((item, index) => (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">{item.treatment_name}</Typography>
                                </TableCell>
                                <TableCell align="right">{item.patient_count}</TableCell>
                                <TableCell align="right">
                                  <Chip label={item.total_count} size="small" color="primary" />
                                </TableCell>
                                <TableCell align="right">
                                  <Chip label={item.completed_count} size="small" color="success" />
                                </TableCell>
                                <TableCell align="right">
                                  <Chip label={item.initiated_count} size="small" color="info" />
                                </TableCell>
                                <TableCell align="right">
                                  <Chip label={item.pending_count} size="small" color="warning" />
                                </TableCell>
                                <TableCell align="right">{item.assigned_count}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && <OPDTreatmentCoordinator />}

      {activeTab === 2 && <TherapistManagement />}

      {/* Enhanced Assign Dialog with Therapist Selection */}
      <Dialog
        open={assignDialog.open}
        onClose={() => {
          setAssignDialog({ ...assignDialog, open: false });
          setShowSlotFull(false);
          setNextAvailableSlots([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Treatment & Schedule Therapist</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Treatment: <strong>{assignDialog.treatmentName}</strong> ({assignDialog.durationMinutes} minutes)
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Scheduled Date"
                value={assignDialog.scheduledDate}
                onChange={(e) => handleDateTimeChange('scheduledDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="time"
                label="Scheduled Time"
                value={assignDialog.scheduledStartTime}
                onChange={(e) => handleDateTimeChange('scheduledStartTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={availableTherapists}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name} - ${option.specialization || 'General'}`}
                value={therapists.find(t => t.id === assignDialog.therapistId) || null}
                onChange={(e, newValue) => {
                  setAssignDialog({
                    ...assignDialog,
                    therapistId: newValue?.id || '',
                    assignedTo: newValue?.id || ''
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign to Therapist"
                    helperText={availableTherapists.length === 0 ? 'No therapists available at this time' : `${availableTherapists.length} therapists available`}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body2">
                        {option.first_name} {option.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.specialization} • {option.experience_years} years exp.
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>

            {/* Next Available Slots */}
            {showSlotFull && nextAvailableSlots.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Selected slot is full. Choose from available slots below:
                </Alert>
                <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Next Available Slots
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {nextAvailableSlots.map((slot, index) => (
                      <Paper
                        key={index}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          border: 1,
                          borderColor: 'primary.main',
                          bgcolor: 'primary.50',
                          '&:hover': {
                            bgcolor: 'primary.100',
                            borderWidth: 2
                          }
                        }}
                        onClick={() => handleSelectNextSlot(slot)}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {new Date(slot.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {slot.startTime.substring(0, 5)} - {slot.endTime.substring(0, 5)}
                            </Typography>
                          </Box>
                          <Chip
                            label={`${slot.availableSlots} slot${slot.availableSlots > 1 ? 's' : ''} available`}
                            size="small"
                            color="success"
                          />
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAssignDialog({ ...assignDialog, open: false });
            setShowSlotFull(false);
            setNextAvailableSlots([]);
          }}>
            Cancel
          </Button>
          <Button onClick={handleAssignSubmit} variant="contained" disabled={!assignDialog.assignedTo}>
            Assign & Book
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={completeDialog.open} onClose={() => setCompleteDialog({ open: false, executionId: null, isBulk: false, ids: [], notes: '' })}>
        <DialogTitle>{completeDialog.isBulk ? 'Bulk Complete Treatments' : 'Complete Treatment'}</DialogTitle>
        <DialogContent sx={{ minWidth: 400, pt: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Completion Notes (Optional)"
            value={completeDialog.notes}
            onChange={(e) => setCompleteDialog({ ...completeDialog, notes: e.target.value })}
            placeholder="Any notes about the treatment completion..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialog({ open: false, executionId: null, isBulk: false, ids: [], notes: '' })}>Cancel</Button>
          <Button onClick={handleCompleteSubmit} variant="contained" color="success">Complete</Button>
        </DialogActions>
      </Dialog>

      {/* Direct Complete Dialog */}
      <Dialog open={directCompleteDialog.open} onClose={() => setDirectCompleteDialog({ open: false, treatmentPlanItemId: null, isBulk: false, ids: [], notes: '' })}>
        <DialogTitle>{directCompleteDialog.isBulk ? 'Bulk Mark Treatments Done' : 'Mark Treatment as Done'}</DialogTitle>
        <DialogContent sx={{ minWidth: 400, pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will mark the treatment as completed without assigning it to a therapist.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Notes (Optional)"
            value={directCompleteDialog.notes}
            onChange={(e) => setDirectCompleteDialog({ ...directCompleteDialog, notes: e.target.value })}
            placeholder="Any notes about the treatment..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDirectCompleteDialog({ open: false, treatmentPlanItemId: null, isBulk: false, ids: [], notes: '' })}>Cancel</Button>
          <Button onClick={handleDirectCompleteSubmit} variant="contained" color="success" startIcon={<CheckCircle />}>Mark Done</Button>
        </DialogActions>
      </Dialog>

      {/* Day treatment plan print (off-screen portal) */}
      {createPortal(
        <div style={{ position: 'fixed', top: 0, left: '-9999px', width: '270mm' }}>
          <div ref={treatmentDayPrintRef} style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#111', background: 'white', padding: '8mm 10mm' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #1b5e20', paddingBottom: 8, marginBottom: 10 }}>
              <img src="/logo.png" alt="MSG Logo" style={{ width: 48, height: 48, objectFit: 'contain', flexShrink: 0 }} />
              <div style={{ flex: 1, textAlign: 'center', padding: '0 8px' }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: '#1b5e20' }}>MSG NATUROPATHY CENTER</div>
                <div style={{ fontSize: 9, color: '#444' }}>Sirsa, Haryana &nbsp;|&nbsp; +91 7082999551 / 7082999351</div>
                <div style={{ fontSize: 13, fontWeight: 700, marginTop: 4 }}>DAY TREATMENT PLAN</div>
                <div style={{ fontSize: 10, color: '#555' }}>Date: {new Date(selectedDate).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</div>
              </div>
              <div style={{ fontSize: 9, color: '#888' }}>Printed: {new Date().toLocaleString('en-IN')}</div>
            </div>

            {/* Summary bar */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              {[
                { label: 'Total Treatments', value: treatments.length, color: '#1565c0' },
                { label: 'Completed', value: treatments.filter(t => t.status === 'completed').length, color: '#2e7d32' },
                { label: 'In Progress', value: treatments.filter(t => t.status === 'in_progress' || t.status === 'initiated').length, color: '#e65100' },
                { label: 'Pending', value: treatments.filter(t => !t.status || t.status === 'pending').length, color: '#c62828' },
              ].map(c => (
                <div key={c.label} style={{ flex: 1, border: `1.5px solid ${c.color}`, borderRadius: 4, padding: '5px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: '#555' }}>{c.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: c.color }}>{c.value}</div>
                </div>
              ))}
            </div>

            {/* Grouped by time slot, then by patient */}
            {['Satkriya', 'Morning', 'Evening', 'Night'].map(slot => {
              const slotItems = treatments.filter(t => t.time_slot === slot);
              if (slotItems.length === 0) return null;
              // Group by patient
              const patientMap = {};
              slotItems.forEach(t => {
                const key = t.patient_code;
                if (!patientMap[key]) patientMap[key] = { ...t, treatments: [] };
                patientMap[key].treatments.push(t.treatment_name);
              });
              const patientRows = Object.values(patientMap);
              const slotLabel = slot === 'Satkriya' ? '🙏 Satkriya' : slot === 'Morning' ? '🌅 Morning' : slot === 'Evening' ? '🌙 Evening' : '🌃 Night';
              return (
                <div key={slot} style={{ marginBottom: 12 }}>
                  <div style={{ background: '#e8f5e9', padding: '4px 8px', fontWeight: 700, fontSize: 11, marginBottom: 2, borderLeft: '4px solid #2e7d32' }}>
                    {slotLabel} ({patientRows.length} patients, {slotItems.length} treatments)
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        {['#', 'Patient', 'Room', 'Treatments', 'Assigned To', 'Status'].map(h => (
                          <th key={h} style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: h === '#' || h === 'Status' ? 'center' : 'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {patientRows.map((t, i) => {
                        const statuses = slotItems.filter(s => s.patient_code === t.patient_code).map(s => s.status || 'pending');
                        const overallStatus = statuses.every(s => s === 'completed') ? 'completed'
                          : statuses.some(s => s === 'in_progress' || s === 'initiated') ? 'in_progress'
                          : 'pending';
                        const statusColor = { completed: '#2e7d32', in_progress: '#e65100', pending: '#c62828' };
                        return (
                          <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'center' }}>{i + 1}</td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 5px', fontWeight: 600 }}>{t.first_name} {t.last_name}</td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{t.room_number}</td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{t.treatments.join(', ')}</td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{t.assigned_to_name || '—'}</td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'center', color: statusColor[overallStatus], fontWeight: 600 }}>
                              {overallStatus.replace('_', ' ').toUpperCase()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </Box>
  );
}

export default TreatmentCoordinatorDashboard;
